"use client";

import React, { useEffect, useMemo } from "react";
import _sortBy from "lodash/sortBy";
import {
  Flex,
  Text,
  buildPath,
  SearchFilterInput,
  AssetView,
  Button,
  PopupMenu,
  SpacePermission,
  useNavigation,
  buildURL,
} from "@thenounproject/lingo-core";
import _isEqual from "lodash/isEqual";
import styled from "styled-components";

import { useAssetLibraryContext } from "../../../contexts/AssetLibraryProvider";
import { SearchSortTypes } from "../types";
import { NavPointTypes } from "@redux/navPoint/NavPoint";
import useNavPoint from "@hooks/useNavPoint";

import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useAssetViewsDelete from "@redux/actions/views/useDeleteView";
import useNotifications from "@actions/useNotifications";
import { useShowCreateEditAssetViewModal } from "../modals/CreateEditAssetViewModal";
import { SubNavButton, SubNavTitle, SubNavWrapper } from "../../../components/navigation/SubNav";
import useUpsell, { AccessStatus } from "@hooks/useUpsell";

type NavItem = {
  title: string;
  onClick: () => void;
  onDelete?: () => void;
  active: boolean;
  view?: AssetView;
  isPicker?: boolean;
};

export const filtersAreEqual = (a: SearchFilterInput[], b: SearchFilterInput[]) => {
  return _isEqual(a, b);
};

type Props = {
  // Only shows query presets
  // Other features like tag management are hidden.
  isPicker?: boolean;
};

const ButtonWrapper = styled.div`
  .overflow {
    opacity: 0;
    transition: all 0.3s ease;
  }
  &:hover {
    .overflow {
      opacity: 1;
    }
  }
`;

const NavItemComponent: React.FC<NavItem> = item => {
  const { showModal } = useShowModal();
  const [showMenu, setShowMenu] = React.useState(false);
  const [deleteAssetView] = useAssetViewsDelete();
  const showAssetViewsModal = useShowCreateEditAssetViewModal();
  return (
    <ButtonWrapper
      style={{ position: "relative" }}
      data-testid={item.title.split(" ").join("-").toLowerCase()}>
      <SubNavButton
        onClick={item.onClick}
        active={item.active}
        data-testid={item.active ? "active-nav-item" : undefined}>
        <Text truncate>{item.title || "Unnamed view"}</Text>
      </SubNavButton>
      {item.view && !item.isPicker && (
        <>
          <Button
            data-popup-source={String(item.view.id).concat("-overflow")}
            onClick={() => setShowMenu(true)}
            className="overflow"
            icon="navigation.overflow"
            size="small"
            position="absolute"
            themeOverrides={{
              primaryColor: "transparent",
              primaryColorDark: "transparent",
              primaryColorTint: "black",
            }}
            right={0}
            top={2}
          />
          <PopupMenu
            open={showMenu}
            close={() => setShowMenu(false)}
            source={String(item.view.id).concat("-overflow")}>
            <PopupMenu.Item
              title="Rename"
              onClick={() => showAssetViewsModal({ view: item.view })}
            />
            <PopupMenu.Item
              title="Delete"
              onClick={() =>
                showModal(ModalTypes.CONFIRMATION, {
                  title: "Delete View",
                  message: "Are you sure? This will delete the custom view from the sidebar.",
                  buttonText: "Delete",
                  buttonProcessingText: "Deleting...",
                  onConfirm: async () => {
                    const result = await deleteAssetView({
                      spaceId: item.view.spaceId,
                      viewId: item.view.id,
                    });
                    if (!result.error) {
                      item.onDelete?.();
                    }
                    return result;
                  },
                })
              }
            />
          </PopupMenu>
        </>
      )}
    </ButtonWrapper>
  );
};

const NavItemMemo = React.memo(NavItemComponent) as typeof NavItemComponent;

const AssetLibraryNav: React.FC<Props> = ({ isPicker = false }) => {
  const navigate = useNavigation();
  const navPoint = useNavPoint();
  const { space } = navPoint;
  const {
    setFilters,
    setSortBy,
    customViews,
    activeView,
    presetViews,
    activePreset,
    viewId,
    setPickerViewId,
  } = useAssetLibraryContext();
  const libraryPath = buildPath("/library/", { space });
  const { showNotification } = useNotifications();
  const isViewingTagsOrFields =
    navPoint?.type === NavPointTypes.TagManagement || navPoint?.type === NavPointTypes.CustomFields;

  useEffect(() => {
    if (typeof viewId === "string" && !presetViews.isLoading && !activePreset) {
      showNotification({ message: "Asset view not found.", level: "error" });
    } else if (typeof viewId === "number" && !customViews.isLoading && !activeView) {
      showNotification({ message: "Asset view not found.", level: "error" });
    }
  }, [
    viewId,
    presetViews.isLoading,
    customViews.isLoading,
    activePreset,
    activeView,
    showNotification,
  ]);

  const presetNavItems: NavItem[] = React.useMemo(() => {
    return (
      presetViews?.data?.map(p => ({
        id: p.id,
        title: p.name,
        onClick: () => {
          if (isPicker) {
            setPickerViewId(p.id);
          } else {
            navigate.push(buildURL("/library", { space }, { view: p.id }));
          }
        },
        active: !isViewingTagsOrFields && p.name === activePreset?.name && !activeView,
      })) || []
    );
  }, [
    presetViews?.data,
    isViewingTagsOrFields,
    activePreset?.name,
    activeView,
    isPicker,
    setPickerViewId,
    navigate,
    space,
  ]);

  const customNavItems: NavItem[] = React.useMemo(() => {
    return (
      customViews.data?.map(p => ({
        title: p.name,
        onClick: () => {
          if (isPicker) {
            setPickerViewId(p.id);
          } else {
            navigate.push(buildPath(`/library`, { space }, { view: p.id }));
          }
        },
        onDelete: () => {
          navigate.push(libraryPath);
          setFilters([]);
        },
        active: p.id === viewId,
        view: p,
        isPicker,
      })) || []
    );
  }, [
    customViews.data,
    viewId,
    isPicker,
    navigate,
    space,
    setFilters,
    setPickerViewId,
    libraryPath,
  ]);

  const navItems = useMemo(
    () => [
      {
        title: "All assets",
        onClick: () => {
          setFilters([]);
          setSortBy(SearchSortTypes.recentDesc);
          if (isPicker) {
            setPickerViewId(null);
          } else {
            navigate.push(libraryPath);
          }
        },
        active: !isViewingTagsOrFields && !activePreset && !activeView,
      },
      ...presetNavItems,
      ...customNavItems,
    ],
    [
      activePreset,
      activeView,
      customNavItems,
      navigate,
      isPicker,
      isViewingTagsOrFields,
      libraryPath,
      presetNavItems,
      setFilters,
      setPickerViewId,
      setSortBy,
    ]
  );

  const renderNavItems = () => {
    return navItems.map((f, i) => {
      return <NavItemMemo key={i} {...f} />;
    });
  };

  const renderNavHeading = (text: string) => (
    <Text mt="l" mb="s" font="ui.smallBold" color="grayDark">
      {text}
    </Text>
  );

  const tagManagerAccess =
    useUpsell(SpacePermission.manageTags).accessStatus !== AccessStatus.insufficientRole;
  const customFieldAccess =
    useUpsell(SpacePermission.editFields).accessStatus !== AccessStatus.insufficientRole;

  const shouldSeeMetadataNav = !isPicker && (tagManagerAccess || customFieldAccess);

  return (
    <SubNavWrapper>
      <SubNavTitle>Library</SubNavTitle>
      <Flex
        px="l"
        pb="l"
        flexDirection="column"
        width={"100%"}
        data-inspector-clear="true"
        overflow="auto">
        {renderNavHeading("Assets")}
        <Flex as="nav" flexDirection="column" gap="8px">
          {renderNavItems()}
        </Flex>
        {shouldSeeMetadataNav && (
          <>
            {renderNavHeading("Metadata")}
            <Flex as="nav" width="100%" flexDirection="column" gap="8px">
              {tagManagerAccess && (
                <SubNavButton
                  key="Tags"
                  active={navPoint?.type === NavPointTypes.TagManagement}
                  onClick={() => navigate.push(buildPath("/library/tags", { space }))}>
                  Tags
                </SubNavButton>
              )}
              {customFieldAccess && (
                <SubNavButton
                  key="Custom Fields"
                  active={navPoint?.type === NavPointTypes.CustomFields}
                  onClick={() => navigate.push(buildPath("/library/fields", { space }))}>
                  Custom Fields
                </SubNavButton>
              )}
            </Flex>
          </>
        )}
      </Flex>
    </SubNavWrapper>
  );
};

export default AssetLibraryNav;
